import type { UserPreferences } from '@projektverwaltung/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from './api.js';
import {
  PREFERENCES_QUERY_KEY,
  type StickyFilterController,
  type StickyFilterValues,
  type StickyFilterView,
  createStickyFilterController,
  mergeStickyFilters,
  readStickyFilters,
  stableStringify,
} from './sticky-filters.js';

type PreferencesResponse = { preferences: UserPreferences };

export type UseStickyFiltersOptions<V extends StickyFilterView> = {
  /** Aktueller Filterstand der Sicht (aus dem Komponenten-State gebaut). */
  values: StickyFilterValues<V>;
  /** Gespeicherte Werte in den Komponenten-State übernehmen (genau einmal pro Mount). */
  apply: (stored: StickyFilterValues<V>) => void;
  /**
   * Einmalige Übernahme eines bisher nur lokal (localStorage) gemerkten Wertes.
   * Wird nur gelesen, solange am Konto für dieses Feld nichts steht.
   */
  local?: Partial<StickyFilterValues<V>> | null;
  /**
   * Solange `false`, wird weder vorbelegt noch gespeichert (Default: `true`).
   *
   * Gedacht für Sichten, deren Filterwerte erst mit den Nutzdaten sinnvoll werden
   * — die Übergabe-Ansicht kennt ihr Segment z. B. erst, wenn die Projekte da
   * sind. Ohne das Gate schriebe sie beim Erstbesuch zuerst den leeren
   * Zwischenstand und gleich danach den korrigierten ans Konto.
   */
  enabled?: boolean;
};

export type StickyFiltersState = {
  /** true, sobald die gespeicherten Werte übernommen wurden. */
  hydrated: boolean;
  /** true, wenn am Konto bereits Werte für diese Sicht lagen. */
  hadStored: boolean;
  /**
   * true, wenn die Präferenz-Anfrage fehlgeschlagen ist. Dann kommt keine
   * Hydration mehr — Sichten, die ihre eigenen Vorbelegungen (z. B.
   * Persona-Defaults) hinter `hydrated` zurückhalten, müssen sie hier trotzdem
   * greifen lassen, sonst kostet ein Fehler beim Laden der Präferenzen mehr als
   * nur die gemerkten Filter.
   */
  failed: boolean;
};

/**
 * Sticky Filters für eine Sicht (Issue #63).
 *
 * DIE EINZIGE Stelle, die Filter persistiert — keine Sicht baut die Mechanik nach.
 * Warum die drei Mechanismen dahinter unverzichtbar sind, steht ausführlich in
 * `sticky-filters.ts`.
 *
 * Nutzung: Filter bleiben ganz normaler `useState` der Sicht. Der Hook bekommt
 * den aktuellen Stand (`values`) und eine Funktion, die einen gespeicherten Stand
 * in den State schreibt (`apply`).
 *
 * Die Freitext-Suche gehört bewusst in KEINEN `values`-Snapshot: eine gemerkte
 * Suche sähe beim nächsten Aufruf wie eine leere Liste aus.
 */
export function useStickyFilters<V extends StickyFilterView>(
  view: V,
  options: UseStickyFiltersOptions<V>,
): StickyFiltersState {
  const queryClient = useQueryClient();
  // Gleicher Key wie in Phase Board und Einstellungen → react-query dedupliziert,
  // es entsteht keine zusätzliche Anfrage.
  const prefsQuery = useQuery({
    queryKey: PREFERENCES_QUERY_KEY,
    queryFn: () => api.getMyPreferences(),
  });
  const persistMutation = useMutation({
    mutationFn: (patch: UserPreferences) => api.updateMyPreferences(patch),
    // Cache mit der Server-Wahrheit synchron halten.
    onSuccess: (res) => queryClient.setQueryData(PREFERENCES_QUERY_KEY, res),
  });

  // Refs statt Dependencies: die Callbacks der Sicht werden bei jedem Render neu
  // erzeugt: als Effekt-Dependency würden sie den Effekt endlos neu starten.
  const applyRef = useRef(options.apply);
  applyRef.current = options.apply;
  const valuesRef = useRef(options.values);
  valuesRef.current = options.values;
  const localRef = useRef(options.local);
  const persistRef = useRef(persistMutation.mutate);
  persistRef.current = persistMutation.mutate;

  const enabled = options.enabled ?? true;

  const [state, setState] = useState<{ hydrated: boolean; hadStored: boolean }>({
    hydrated: false,
    hadStored: false,
  });

  const controllerRef = useRef<StickyFilterController<StickyFilterValues<V>> | null>(null);
  if (controllerRef.current === null) {
    controllerRef.current = createStickyFilterController<StickyFilterValues<V>>({
      updateCache: (snapshot) => {
        queryClient.setQueryData<PreferencesResponse>(PREFERENCES_QUERY_KEY, (prev) => ({
          preferences: mergeStickyFilters(prev?.preferences, view, snapshot),
        }));
      },
      persist: (snapshot) => {
        const prev = queryClient.getQueryData<PreferencesResponse>(PREFERENCES_QUERY_KEY);
        const next = mergeStickyFilters(prev?.preferences, view, snapshot);
        // `view_filters` geht vollständig raus. Die API merged es zwar seit dem
        // Review zu #63 selbst je Sicht (`mergePreferences`), aber gegen eine
        // zurückgerollte API wäre ein Teil-Objekt ein Filterverlust.
        persistRef.current({ view_filters: next.view_filters });
      },
    });
  }
  const controller = controllerRef.current;

  const prefs = prefsQuery.data?.preferences;
  // `controller` liegt in einer Ref und `view` ist über die Lebensdauer der Sicht
  // konstant; als Dependencies würden sie nur unnötige Läufe auslösen. Die
  // Vorbelegung soll ausschließlich am neuen Präferenz-Stand hängen.
  // biome-ignore lint/correctness/useExhaustiveDependencies: nur am Praeferenz-Stand haengen
  useEffect(() => {
    if (!enabled || !prefs) return;
    const read = readStickyFilters(prefs, view);
    const didHydrate = controller.hydrate({
      stored: read.values,
      legacy: read.legacy,
      local: localRef.current,
      apply: (values) => applyRef.current(values),
    });
    if (didHydrate) setState({ hydrated: true, hadStored: read.values !== undefined });
  }, [prefs, enabled]);

  // Speichern bei jeder Änderung. `snapshotKey` statt `values` als Dependency:
  // das Objekt ist bei jedem Render neu, sein Inhalt aber meist gleich.
  // `state.hydrated` gehört dazu, damit ein übernommener lokaler Wert direkt nach
  // der Hydration ans Konto wandert.
  const snapshotKey = stableStringify(options.values);
  // `snapshotKey` ist genau die gewollte Dependency (Inhalt statt Objekt-Identität),
  // `state.hydrated` stößt den Write für einen übernommenen lokalen Wert an. Beide
  // werden im Effekt-Körper absichtlich nicht gelesen — der Stand kommt aus der Ref.
  // biome-ignore lint/correctness/useExhaustiveDependencies: Inhalts-Key statt Objekt-Identitaet
  useEffect(() => {
    // Bewusst gegen `state.hydrated` geprüft, NICHT gegen den (synchron gesetzten)
    // Controller-Zustand: der Hydrations-Effekt läuft im selben Commit direkt davor
    // und legt seine Werte per setState vor. Erst im Folge-Commit steht der
    // vorbelegte Stand wirklich in `valuesRef` — vorher würde hier der Stand von
    // VOR der Vorbelegung gespeichert.
    if (!enabled || !state.hydrated) return;
    controller.save(valuesRef.current);
  }, [snapshotKey, state.hydrated, enabled]);

  // Beim Verlassen der Sicht den noch ausstehenden (debounced) Write abschicken.
  // `controller` liegt in einer Ref; der Effekt darf ausschließlich beim Unmount aufräumen.
  // biome-ignore lint/correctness/useExhaustiveDependencies: Aufraeumen nur beim Unmount
  useEffect(() => {
    return () => controller.flush();
  }, []);

  return { ...state, failed: prefsQuery.isError };
}
